/**
 * Visual editor for a `data_list.row_actions` array (ADR-0010 S5b · ADR-0044 U2).
 * Each action is `{label, action, target_page?, open_mode?}`. Open/Edit go to a
 * page (picked from the app's pages) with `?row=<id>`; Delete removes the row;
 * Select writes the row into a page variable.
 *
 * Appsmith-grade readability (U2): each row leads with a plain-Italian SUMMARY
 * ("Apri «Deal» in drawer") and every control carries a field LABEL, so the
 * cramped "Open · Deal · Drawer" gibberish becomes a small labelled mini-form.
 */
import { Plus, Trash2 } from "lucide-react";
import type { ReactNode } from "react";

import { summarizeRowAction } from "@/lib/actionSummary";

export interface RowAction {
  label?: string;
  action?: "open" | "edit" | "delete" | "select";
  target_page?: string;
  open_mode?: "page" | "drawer";
}

const ctl = "rounded border border-border bg-background px-2 py-1 text-sm";

/** A control wrapped with a small caption above it. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

export function RowActionsEditor({
  value,
  pages,
  onChange,
}: {
  value: unknown;
  /** App pages (slug + title) for the target-page picker. */
  pages: { slug: string; title: string }[];
  onChange: (v: RowAction[]) => void;
}) {
  const rows: RowAction[] = Array.isArray(value) ? (value as RowAction[]) : [];

  const patch = (i: number, p: Partial<RowAction>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const add = () =>
    onChange([...rows, { label: "Modifica", action: "edit", open_mode: "drawer" }]);
  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i));

  return (
    <div className="flex flex-col gap-2">
      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground">Nessuna azione di riga.</p>
      )}
      {rows.map((r, i) => {
        // Only open/edit go to a page; delete + select don't take a target.
        const needsTarget = r.action === "open" || r.action === "edit" || r.action == null;
        return (
          <div
            key={i}
            className="flex flex-col gap-2 rounded-md border border-border/60 bg-surface p-2"
          >
            {/* Plain-language summary — what this row's button actually does. */}
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs font-medium text-foreground">
                {summarizeRowAction(r, pages)}
              </span>
              <button
                type="button"
                aria-label="Rimuovi azione"
                className="shrink-0 p-0.5 text-danger"
                onClick={() => remove(i)}
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex flex-wrap items-end gap-1.5">
              <Field label="Etichetta">
                <input
                  className={`${ctl} w-28`}
                  placeholder="es. Apri"
                  value={r.label ?? ""}
                  onChange={(e) => patch(i, { label: e.target.value })}
                />
              </Field>
              <Field label="Azione">
                <select
                  aria-label="Azione"
                  className={ctl}
                  value={r.action ?? "open"}
                  onChange={(e) => patch(i, { action: e.target.value as RowAction["action"] })}
                >
                  <option value="open">Apri</option>
                  <option value="edit">Modifica</option>
                  <option value="delete">Elimina</option>
                  <option value="select">Seleziona</option>
                </select>
              </Field>
              {needsTarget && (
                <>
                  <Field label="Pagina">
                    <select
                      aria-label="Pagina di destinazione"
                      className={ctl}
                      value={r.target_page ?? ""}
                      onChange={(e) => patch(i, { target_page: e.target.value })}
                    >
                      <option value="">— pagina —</option>
                      {pages.map((p) => (
                        <option key={p.slug} value={p.slug}>
                          {p.title || p.slug}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Apertura">
                    <select
                      aria-label="Modalità di apertura"
                      className={ctl}
                      value={r.open_mode ?? "drawer"}
                      onChange={(e) =>
                        patch(i, { open_mode: e.target.value as RowAction["open_mode"] })
                      }
                    >
                      <option value="drawer">Drawer</option>
                      <option value="page">Pagina intera</option>
                    </select>
                  </Field>
                </>
              )}
            </div>
          </div>
        );
      })}
      <button
        type="button"
        className="flex w-fit items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-surface"
        onClick={add}
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
        Aggiungi azione
      </button>
    </div>
  );
}
